import React, { ReactElement, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Resolver, useForm } from 'react-hook-form';
import { initialForgotPasswordFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSchema } from 'pages/ResetPasswordPage/schemaValidation';
import { EButtonVariants } from 'constants/Buttons';
import { RouterLink } from 'components/atoms/RouterLink';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { Box } from '@mui/system';
import { usePassword } from 'models/passwordContext';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { useNavigate } from 'react-router-dom';
import { AuthTemplate, StyledAuthHeader } from 'components/templates/AuthTemplate';
import { useQuery } from 'hooks/useQuery';
import { validateResetPasswordLink } from 'requests/password';
import { FC_SYSTEM_DEVICE_ID } from 'pages/LoginPage';
import { StyledPasswordRulesWrapper } from 'pages/ResetPasswordPage/styles';
import { getDeviceName } from 'helpers/userAgent';
import { EStep } from 'pages/AuthPage';
import { useTranslations } from 'hooks/useTranslations';
import {
  StyledLanguageSelectOuterWrapper,
  StyledLanguageSelectWrapper,
} from 'components/templates/AuthTemplate/styles';
import { LanguageSelect } from 'components/molecules/LanguageSelect';
import { AuthLogoContainer } from 'components/organisms/AuthLogoContainer';

const ResetPasswordPage = (): ReactElement => {
  const [isResetPasswordSuccessfully, setIsResetPasswordSuccessfully] = useState<
    boolean | null
  >(null);
  const [isLoaded, setLoaded] = useState(false);
  const [alertText, setAlertText] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { schema } = useSchema();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialForgotPasswordFormValues,
    ...(isResetPasswordSuccessfully === null
      ? { resolver: yupResolver(schema) as unknown as Resolver }
      : {}),
  });
  const { changePassword, isChangePasswordLoading, isForgotPasswordLoading } = usePassword();

  const query = useQuery();
  const token: any = query.get('token');
  const queryParams: any = new URLSearchParams({
    token: token ? token.toUpperCase() : token,
  });

  const encodedEmail: any = query.get('e');
  let decodedEmail = '';

  try {
    decodedEmail = encodedEmail ? atob(encodedEmail?.toString()) : '';
  } catch (e: any) {
    navigate(ERouteLinks.AuthWithSlash);
  }

  useEffect(() => {
    (async function callValidateResetPasswordLink() {
      try {
        await validateResetPasswordLink(token);
      } catch (e: any) {
        setIsResetPasswordSuccessfully(false);
        if (e.response.status === 410) {
          setAlertText(t('resetPassword.linkExpired.text'));
        } else if (e.response.status === 400) {
          setAlertText(t('resetPassword.linkAlreadyBeenUsed.text'));
        }
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePasswordSubmit = handleSubmit(({ newPassword }: any) => {
    changePassword(
      { newPassword, deviceName: getDeviceName() },
      queryParams,
      (res) => {
        localStorage.removeItem(FC_SYSTEM_DEVICE_ID);
        if (res.accessToken) {
          setIsResetPasswordSuccessfully(true);
        } else {
          navigate(ERouteLinks.AuthWithSlash, {
            state: {
              twoFactorMethod:
                !!res.offlineQRCodeBase64 || res.offlineQRCodeBase64 === null
                  ? EStep.authenticator
                  : EStep.twoFactorAuth,
              email: decodedEmail,
              password: newPassword,
              offlineQRCodeBase64: res.offlineQRCodeBase64,
              isRedirectToTwoFactorAuthPage: true,
            },
          });
        }
      },
      () => {
        setAlertText(t('resetPassword.doesntMeetRequiredCriteria.text'));
      },
    );
  });

  const handleForgotPasswordSubmit = () => {
    navigate(ERouteLinks.ForgotPassword);
  };

  const renderResetPasswordTemplate = () => {
    if (isLoaded) {
      if (isResetPasswordSuccessfully === null) {
        return (
          <>
            <Grid
              size={{ xs: 12 }}
              sx={{
                mt: 4,
                mb: {
                  xs: 2,
                  sm: 3,
                },
              }}
            >
              <Grid size={{ xs: 12 }}>
                <StyledAuthHeader
                  variant="h4"
                  style={{ marginBottom: '16px', textAlign: 'center' }}
                >
                  {t('resetPassword.setNewPassword.label')}
                </StyledAuthHeader>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                  {t('resetPassword.passwordMustHave.label')}
                </Typography>
                <StyledPasswordRulesWrapper>
                  <li>
                    <Typography variant="caption">
                      {t('resetPassword.passwordMustHave.charactersLength.label')}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="caption">
                      {t('resetPassword.passwordMustHave.characterUppercaseAndSpecial.label')}
                    </Typography>
                  </li>
                </StyledPasswordRulesWrapper>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mb: 1 }}>
                <FormInput
                  name="newPassword"
                  type={EFormInputTypes.password}
                  label={t('resetPassword.newPassword.label')}
                  placeholder={t('resetPassword.EnterNewPassword.placeholder')}
                  control={control}
                  errors={errors}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
              </Box>
              <FormInput
                name="newPasswordRepeat"
                type={EFormInputTypes.password}
                label={t('resetPassword.confirmPassword.label')}
                placeholder={t('resetPassword.password.placeholder')}
                control={control}
                errors={errors}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: { xs: 32, sm: 40 },
                  },
                }}
              />
              {alertText && <Alert text={alertText} variant={EAlertVariants.error} />}
            </Grid>
          </>
        );
      }
      if (isResetPasswordSuccessfully) {
        return (
          <>
            <Grid size={{ xs: 12 }} sx={{ mt: 4, mb: 4 }}>
              <StyledAuthHeader style={{ marginBottom: '16px' }}>
                Password Reset
              </StyledAuthHeader>
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ mb: 4 }}>
              <Alert text={t('resetPassword.passwordChangedSuccessfully.label')} />
            </Grid>
            <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'center' }}>
              <Grid size={{ xs: 12 }}>
                <Button
                  label={t('resetPassword.continue.label')}
                  variant={EButtonVariants.contained}
                  onClick={() => navigate(ERouteLinks.Dashboard)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </>
        );
      }
      if (!isResetPasswordSuccessfully && alertText) {
        return (
          <>
            <Grid size={{ xs: 12 }} sx={{ my: 4 }}>
              <StyledAuthHeader style={{ marginBottom: '16px' }}>
                Set New Password
              </StyledAuthHeader>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mb: 2 }}>
                <Alert text={alertText} variant={EAlertVariants.warning} />
              </Box>
            </Grid>
            <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'center' }}>
              <Grid size={{ xs: 12 }} sx={{ my: 3 }}>
                <Button
                  label={t('resetPassword.resendLink.label')}
                  variant={EButtonVariants.outlined}
                  onClick={() => handleForgotPasswordSubmit()}
                  isLoading={isForgotPasswordLoading}
                  fullWidth
                />
              </Grid>
              <RouterLink
                to={ERouteLinks.AuthWithSlash}
                label={t('resetPassword.backLogin.button')}
              />
            </Grid>
          </>
        );
      }
    }
    return null;
  };

  if (isLoaded) {
    return (
      <AuthTemplate>
        <Grid
          container
          size={{ xs: 12, sm: 10, md: 7, lg: 6 }}
          sx={{
            display: 'grid',
            py: 2,
            px: { xs: 2, xl: 20 },
            justifyContent: 'center',
            justifyItems: 'center',
            alignItems: 'flex-start',
            alignContent: 'stretch',
            pb: 5,
          }}
        >
          <Grid size={{ xs: 12 }}>
            <StyledLanguageSelectOuterWrapper>
              <StyledLanguageSelectWrapper>
                <LanguageSelect isVisible />
              </StyledLanguageSelectWrapper>
            </StyledLanguageSelectOuterWrapper>
            <AuthLogoContainer />
          </Grid>

          <Grid
            container
            size={{ xs: 12 }}
            sx={{ justifyContent: 'center', alignItems: 'flex-start' }}
          >
            <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'center' }}>
              {renderResetPasswordTemplate()}
            </Grid>
            {isResetPasswordSuccessfully === null && (
              <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'center' }}>
                <Grid size={{ xs: 12 }} sx={{ my: 3 }}>
                  <Button
                    label={t('resetPassword.setNewPassword.button')}
                    variant={EButtonVariants.contained}
                    onClick={() => handleChangePasswordSubmit()}
                    isLoading={isChangePasswordLoading}
                    fullWidth
                  />
                </Grid>
                <RouterLink
                  to={ERouteLinks.AuthWithSlash}
                  label={t('resetPassword.backLogin.button')}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </AuthTemplate>
    );
  }
  return <div />;
};
export { ResetPasswordPage };
